import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProject, resolveImg } from "@/lib/api";
import { Nav } from "@/components/td/Nav";
import { Footer } from "@/components/td/Footer";
import { useContent } from "@/context/ContentContext";
import { Loader2 } from "lucide-react";

const Block = ({ label, text, index }) => {
  if (!text) return null;
  return (
    <div className="split" style={{ gap: "clamp(24px,4vw,72px)", padding: "clamp(32px,4vw,56px) 0", borderTop: "2px solid currentColor" }}>
      <div>
        <p className="lb" style={{ color: "var(--bl)" }}>{String(index + 1).padStart(2, "0")} · {label}</p>
      </div>
      <p className="lead" style={{ maxWidth: 900 }}>{text}</p>
    </div>
  );
};

export default function CaseDetail() {
  const { id } = useParams();
  const { contacto } = useContent();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    getProject(id)
      .then(setProject)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="td">
      <Nav solid />

      {loading ? (
        <div className="dk" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
          <Loader2 className="animate-spin" size={32} />
        </div>
      ) : notFound || !project ? (
        <div className="dk" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", gap: 24, alignItems: "center", justifyContent: "center" }}>
          <p className="h h3">Caso no encontrado</p>
          <Link to="/#casos" className="btn" data-testid="case-back-link">Volver a casos</Link>
        </div>
      ) : (
        <main data-testid="case-detail">
          <section className="dk" style={{ padding: "clamp(120px,14vw,180px) 0 clamp(48px,6vw,80px)" }}>
            <div className="wrap">
              <Link to="/#casos" className="lb" style={{ color: "var(--or)" }} data-testid="case-back-link">← Casos</Link>
              <p className="lb" style={{ marginTop: 32 }}>Caso · {project.brand}</p>
              <h1 className="h h2" style={{ marginTop: 20, maxWidth: 1300 }} data-testid="case-title">{project.title}</h1>
              {project.subtitle && <p className="lead mut" style={{ marginTop: 24 }}>{project.subtitle}</p>}
              {project.category && (
                <div className="chips" style={{ marginTop: 28 }}>
                  {project.category.split("·").map((c, i) => <span className="chip" key={i}>{c.trim()}</span>)}
                </div>
              )}
            </div>
          </section>

          {project.image_url && (
            <section className="dk">
              <img
                src={resolveImg(project.image_url)}
                alt={`${project.brand} — ${project.title}`}
                style={{ width: "100%", height: "min(70vh,760px)", objectFit: "cover" }}
                data-testid="case-image"
              />
            </section>
          )}

          <section className="sec bn-sec">
            <div className="wrap">
              <Block label="Contexto" text={project.contexto} index={0} />
              <Block label="Idea" text={project.idea} index={1} />
              <Block label="Impacto" text={project.impacto} index={2} />
            </div>
          </section>

          <section className="sec or on-bn">
            <div className="wrap" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: 32 }}>
              <div>
                <h2 className="h h2">¿Tienes un proyecto <span className="ac">así?</span></h2>
                <p className="lead" style={{ marginTop: 20 }}>Construyamos el siguiente caso juntos.</p>
              </div>
              <a href={`mailto:${contacto?.email || "hola@somostaandem.com"}`} className="btn ink" data-testid="case-cta">Hablemos</a>
            </div>
          </section>
        </main>
      )}

      <Footer />
    </div>
  );
}
